class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.classList.add('hidden');

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

class QuantityInputDawn extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

if (!customElements.get('quantity-input-dawn')) {
  customElements.define('quantity-input-dawn', QuantityInputDawn);
}

class ProductVariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.clearInitialSelection();

    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);
      this.syncProductFormFromCurrentSelection();

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  clearInitialSelection() {
    if (this.dataset.preselectVariant === "true") return;
    if (this.querySelector('fieldset input:checked, select option[selected]:not([value=""])')) return;
    if (this.closest("product-info")?.querySelector('product-form input[name="id"]')?.value) return;

    this.querySelectorAll('fieldset input:checked').forEach(input => {
      input.checked = false;
      input.removeAttribute("checked");
    });

    this.querySelectorAll('select').forEach(select => {
      const placeholderOption = Array.from(select.options).find(option => option.value === "");
      if (!placeholderOption) return;

      Array.from(select.options).forEach(option => option.removeAttribute("selected"));
      placeholderOption.setAttribute("selected", "selected");
      select.value = "";
      select.closest("dropdown-input")?.update();
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;
    this.clearVariantPickerError(target.closest(".product-form__input"));

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      const previousSelectedOption = Array.from(target.options)
        .find((option) => option.getAttribute('selected'));
      previousSelectedOption?.removeAttribute('selected');

      // change previous target.selectedOptions with new value
      const newSelectedOption = Array.from(target.options).find((option) => option.value === value);
      if (newSelectedOption) {
        newSelectedOption.setAttribute('selected', 'selected');
      }

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .product-option__swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('product-option__swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('product-option__swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      this.querySelectorAll('input[type="radio"]').forEach(input => {
        if (input.name !== target.name) return;
        input.toggleAttribute("checked", input === target);
      });
      target.checked = true;

      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  clearVariantPickerError(optionGroup) {
    if (!optionGroup) return;

    optionGroup.classList.remove("has-error");
    this.closest("product-info")?.querySelector("product-form")?.clearButtonError?.();
  }

  syncProductFormFromCurrentSelection() {
    const productInfo = this.closest("product-info");
    const productForm = productInfo?.querySelector("product-form");
    const variantInput = productForm?.variantIdInput;
    if (!productForm || !variantInput) return;

    const selectedOptions = this.selectedOptionNames;
    if (!selectedOptions.length || selectedOptions.some(option => !option)) {
      variantInput.value = "";
      productForm.setSoftDisabledButtonState?.();
      return;
    }

    const selectedVariant = this.variants.find(variant =>
      selectedOptions.every((option, index) =>
        String(variant.options[index]).trim() === String(option).trim()
      )
    );

    if (!selectedVariant) {
      variantInput.value = "";
      productForm.setSoftDisabledButtonState?.(window.variantStrings.unavailable);
      return;
    }

    variantInput.value = selectedVariant.id;
    productForm.toggleSubmitButton(!selectedVariant.available, window.variantStrings.soldOut);
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionNames() {
    return Array.from(this.querySelectorAll(".product-form__input")).map(optionGroup => {
      const select = optionGroup.querySelector("select");
      if (select) return select.value;

      const checkedInput = optionGroup.querySelector('input:checked:not([data-size-chart-size-radio])');
      return checkedInput?.value || "";
    });
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    ).filter(Boolean);
  }

  get variants() {
    if (this.variantData) return this.variantData;

    const variantsJson = this.querySelector("[data-all-variants]")?.textContent;
    if (!variantsJson) return [];

    try {
      this.variantData = JSON.parse(variantsJson);
    } catch (error) {
      this.variantData = [];
    }

    return this.variantData;
  }
}
if (!customElements.get('product-variant-selects')) {
  customElements.define('product-variant-selects', ProductVariantSelects);
}

const productInfoSectionCache = new Map();

if (!customElements.get('product-info')) {
  customElements.define(
    "product-info",
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector(".quantity__input");
        this.productMediaGallery = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        this.productVariantSelects = this.querySelector('product-variant-selects');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.addEventListener(
          'click',
          this.handleProductGroupClick
        );

        this.addEventListener(
          'pointerover',
          this.prefetchProductGroup
        );

        this.addEventListener(
          'touchstart',
          this.prefetchProductGroup,
          { passive: true }
        );

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        this.dispatchEvent(
          new CustomEvent("product-info:loaded", {
            bubbles: true
          })
        );

        this.initUpdateMedia();

        if (
          this.productMediaGallery.dataset
            .hideOtherVariantsMedia === 'true'
        ) {
          this.updateVariantMedia();
        }
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector(
          ".product-form__quantity"
        );
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      initUpdateMedia() {
        const variantIdFromUrl = parseInt(window.location.search.split('?').filter(item => item.includes('variant='))[0]?.split('=')[1]) || null;
        if (!variantIdFromUrl) { return; }
        const variant = this.getSelectedVariant(this);
        if (!variant || !variantIdFromUrl) { return; }
        if (variant?.id === variantIdFromUrl) {
          this.updateMedia(variant?.featured_media?.id);
        }
      }

      disconnectedCallback() {
        this.removeEventListener(
          'click',
          this.handleProductGroupClick
        );

        this.removeEventListener(
          'pointerover',
          this.prefetchProductGroup
        );

        this.removeEventListener(
          'touchstart',
          this.prefetchProductGroup
        );

        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push(html =>
          html
            .querySelectorAll(".scroll-trigger")
            .forEach(element =>
              element.classList.add("scroll-trigger--cancel")
            )
        );
        this.postProcessHtmlCallbacks.push(newNode => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      getProductGroupRequestUrl(link) {
        const productUrl =
          link?.dataset?.productUrl;

        if (!productUrl) return '';

        return this.buildRequestUrlWithParams(
          productUrl,
          [],
          false
        );
      }

      prefetchProductGroup = (event) => {
        const colorLink = event.target.closest(
          '.product-option__link[data-product-url]'
        );

        if (
          !colorLink ||
          !this.contains(colorLink)
        ) {
          return;
        }

        this.prefetchProductGroupLink(colorLink);
      };

      prefetchProductGroupLink(link) {
        const requestUrl =
          this.getProductGroupRequestUrl(link);

        if (
          !requestUrl ||
          productInfoSectionCache.has(requestUrl)
        ) {
          return;
        }

        const request = fetch(requestUrl)
          .then(response => {
            if (!response.ok) {
              throw new Error(
                `Product section request failed: ${response.status}`
              );
            }

            return response.text();
          })
          .then(responseText => {
            productInfoSectionCache.set(
              requestUrl,
              responseText
            );

            return responseText;
          })
          .catch(() => {
            productInfoSectionCache.delete(
              requestUrl
            );

            return null;
          });

        /*
        * Store the pending request too.
        * If user clicks while preload is still running,
        * the same request is reused.
        */
        productInfoSectionCache.set(
          requestUrl,
          request
        );
      }

      handleProductGroupClick = (event) => {
        const colorLink = event.target.closest(
          '.product-option__link[data-product-url]'
        );

        if (!colorLink || !this.contains(colorLink)) {
          return;
        }

        event.preventDefault();

        const productUrl =
          colorLink.dataset.productUrl;

        if (!productUrl) return;

        this.pendingRequestUrl = productUrl;

        this.renderProductInfo({
          requestUrl:
            this.buildRequestUrlWithParams(
              productUrl,
              [],
              false
            ),

          targetId: '',

          callback:
            this.handleSwapProduct(
              productUrl,
              false,
              true
            )
        });
      };

      handleOptionValueChange({
        data: { event, target, selectedOptionValues }
      }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        // const shouldFetchFullPage =
        //   this.dataset.updateUrl === "true" && shouldSwapProduct;

        const shouldFetchFullPage = false;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl)
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        if (productForm?.requiresVariantSelection && !productForm.hasSelectedVariant) {
          productForm.setSoftDisabledButtonState();
        }
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(
        productUrl,
        updateFullPage,
        resetRequiredSelection = false
      ) {
        return html => {
          this.productModal?.remove();

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : "product-info";

          const incomingProductInfo =
            html.querySelector(selector);

          const variant =
            this.getSelectedVariant(
              incomingProductInfo
            );

          /*
          * Product-group / color swap:
          * don't put Shopify's default variant in URL.
          *
          * Normal theme variant/product swap:
          * retain existing behaviour.
          */
          this.updateURL(
            productUrl,
            resetRequiredSelection
              ? null
              : variant?.id
          );

          const postProcessCallbacks =
            resetRequiredSelection
              ? [
                  ...this.postProcessHtmlCallbacks,

                  newNode => {
                    const productForm =
                      newNode.querySelector(
                        'product-form'
                      );

                    if (
                      !productForm ||
                      !productForm.requiresVariantSelection
                    ) {
                      return;
                    }

                    /*
                    * The new grouped product is rendered by
                    * Shopify with a default hidden variant ID.
                    *
                    * But Size is intentionally unselected in
                    * this theme, so don't allow that default ID
                    * to become the customer's selection.
                    */
                    if (
                      productForm
                        .hasMissingRequiredSize?.()
                    ) {
                      if (
                        productForm.variantIdInput
                      ) {
                        productForm.variantIdInput.value =
                          "";
                      }

                      productForm
                        .setSoftDisabledButtonState?.();
                    }
                  }
                ]
              : this.postProcessHtmlCallbacks;

          if (updateFullPage) {
            document.querySelector(
              "head title"
            ).innerHTML =
              html.querySelector(
                "head title"
              ).innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector("main"),
              html.querySelector("main"),
              this.preProcessHtmlCallbacks,
              postProcessCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              incomingProductInfo,
              this.preProcessHtmlCallbacks,
              postProcessCallbacks
            );
          }
        };
      }

      renderProductInfo({
        requestUrl,
        targetId,
        callback
      }) {
        this.abortController?.abort();
        this.abortController =
          new AbortController();

        const renderResponse = responseText => {
          if (!responseText) return;

          this.pendingRequestUrl = null;

          const html =
            new DOMParser().parseFromString(
              responseText,
              "text/html"
            );

          callback(html);

          if (targetId) {
            document
              .getElementById(targetId)
              ?.focus();
          }
        };

        const cached =
          productInfoSectionCache.get(
            requestUrl
          );

        /*
        * Already downloaded.
        */
        if (typeof cached === 'string') {
          renderResponse(cached);
          return;
        }

        /*
        * Prefetch currently in progress.
        * Reuse the same request.
        */
        if (
          cached &&
          typeof cached.then === 'function'
        ) {
          cached
            .then(responseText => {
              if (responseText) {
                renderResponse(responseText);
              }
            })
            .catch(() => {});

          return;
        }

        const request = fetch(
          requestUrl,
          {
            signal:
              this.abortController.signal
          }
        )
          .then(response => {
            if (!response.ok) {
              throw new Error(
                `Product section request failed: ${response.status}`
              );
            }

            return response.text();
          })
          .then(responseText => {
            productInfoSectionCache.set(
              requestUrl,
              responseText
            );

            renderResponse(responseText);

            return responseText;
          })
          .catch(error => {
            productInfoSectionCache.delete(
              requestUrl
            );

            if (
              error.name !== "AbortError"
            ) {
              console.error(error);
            }

            return null;
          });

        productInfoSectionCache.set(
          requestUrl,
          request
        );
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector(
          "product-variant-selects [data-selected-variant]"
        )?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false
      ) {
        const params = [];

        !shouldFetchFullPage &&
          params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(",")}`);
        }

        return `${url}?${params.join("&")}`;
      }

      updateOptionValues(html, variant) {
        const productVariantSelects = html.querySelector("product-variant-selects");
        if (productVariantSelects) {
          this.markSelectedOptions(productVariantSelects, variant);
          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            productVariantSelects,
            this.preProcessHtmlCallbacks
          );
        }
      }

      handleUpdateProductInfo(productUrl) {
        return html => {
          const variant = this.getSelectedVariant(html);

          // this.pickupAvailability?.update(variant);
          this.updateOptionValues(html, variant);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);
          this.updateVariantDiscountBadge(variant?.price, variant?.compare_at_price);
          this.updateVariantDetails(variant?.id);
          if (this.productMediaGallery.dataset.hideOtherVariantsMedia === 'true') {
            this.updateVariantMedia(html);
          }

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(variant?.featured_media?.id);

          const updateSourceFromDestination = (
            id,
            shouldHide = source => false
          ) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);

            if (source && destination) {

              destination.innerHTML = source.innerHTML;

              if (id === 'Inventory') {
                const inventoryThreshold = parseInt(source.dataset.inventoryThreshold);
                const inventoryQuantity = parseInt(source.dataset.inventoryQuantity);
                destination.classList.toggle("hidden", inventoryThreshold && inventoryQuantity > inventoryThreshold || inventoryQuantity < 1);
              } else {
                destination.classList.toggle("hidden", shouldHide(source));
              }
            }
          };

          updateSourceFromDestination("ProductPurchaseInfo");
          updateSourceFromDestination("ProductStickyCart");

          updateSourceFromDestination("price");
          updateSourceFromDestination("PaymentPrices");
          updateSourceFromDestination("Sku", ({ classList }) => classList.contains("hidden"));

          updateSourceFromDestination("Inventory", ({ innerText }) => innerText === "");

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(
            `#Quantity-Rules-${this.dataset.section}`
          )?.classList.remove("hidden");

          this.productForm?.toggleSubmitButton(
            !variant.available,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant
            }
          });
        };
      }

      markSelectedOptions(productVariantSelects, variant) {
        if (!variant?.options?.length) return;

        productVariantSelects.querySelectorAll(".product-form__input").forEach((optionGroup, index) => {
          const selectedValue = variant.options[index];
          if (!selectedValue) return;

          const selectedValueLabel = optionGroup.querySelector("[data-selected-value]");
          if (selectedValueLabel && !selectedValueLabel.querySelector(".product-option__swatch")) {
            selectedValueLabel.textContent = selectedValue;
          }

          optionGroup.querySelectorAll('input[type="radio"]').forEach(input => {
            const isSelected = input.value === selectedValue;
            input.checked = isSelected;
            input.toggleAttribute("checked", isSelected);
          });

          optionGroup.querySelectorAll("select").forEach(select => {
            Array.from(select.options).forEach(option => {
              option.toggleAttribute("selected", option.value === selectedValue);
            });
            select.value = selectedValue;
          });
        });
      }

      updateVariantMedia() {
        const select_variant_options_handle = this.querySelector(
          'product-variant-selects [data-selected-variant-options-handle]'
        )?.innerHTML.trim();

        if (!select_variant_options_handle) {
          const productAllVariants = this.productVariantSelects.querySelector('[data-all-variants]').innerHTML.trim();

          // Make sure to attach the event listener to the correct element
          this.productVariantSelects.addEventListener('click', (event) => {
            const closestVariantOptionInput = event.target.closest('.product-option__input');

            // Ensure that you are finding the correct input
            if (closestVariantOptionInput) {
              const closestVariantOptionInputValue = closestVariantOptionInput.value;

              // Parse productAllVariants and check for the selected variant
              const availableVariant = JSON.parse(productAllVariants).find(variant => {
                return variant.options.some(option => option === closestVariantOptionInputValue);
              });
            } else {
              // console.log('No variant option input found');
            }
          });
        }

        this.productMediaGallery.querySelectorAll('[data-media-group]').forEach(element => element.classList.add('hidden'));

        this.productMediaGallery.querySelectorAll(`[data-media-group="${select_variant_options_handle}"]`).forEach(element => element.classList.remove('hidden'));

        this.productMediaGallery.querySelectorAll('swiper-product-gallery').forEach(swiperSlide => {
          if (swiperSlide.instance) {
            swiperSlide.instance.update(); // Update the Swiper instance
          } else {
            swiperSlide.init(); // If the instance is not available, reinitialize
          }
        });
      }

      updateVariantOptionMedia() {
        this.productMediaGallery.querySelectorAll('[data-media-group]:not(.hidden)').forEach(element => {

        });
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach(productForm => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? "";
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector("share-button")?.updateUrl(
          `${window.shopUrl}${url}${
            variantId ? `?variant=${variantId}` : ""
          }`
        );

        if (this.dataset.updateUrl === "false") return;
        window.history.replaceState(
          {},
          "",
          `${url}${variantId ? `?variant=${variantId}` : ""}`
        );
      }

      updateSellingPlanURL(sellingPlanId, selectedVariantId) {
        if (!selectedVariantId) return;
        if (!sellingPlanId) {
          window.history.replaceState({}, "", `${window.location.href.replace(/&selling_plan=\d+/, "")}`);
        } else {
          const params = new URLSearchParams(window.location.search);
          params[params.has("variant") ? "set" : "append"](
            "variant",
            selectedVariantId
          );
          if (sellingPlanId) {
            params[params.has("selling_plan") ? "set" : "append"](
              "selling_plan",
              sellingPlanId
            );
          } else {
            params.delete("selling_plan");
          }
          window.history.replaceState({}, "", `${this.dataset.url}?${params.toString()}`);
        }
      }

      updateVariantDetails(variantId) {
        const productVariantDetails = this.querySelectorAll('.product__metafield_for_variant-block');
        if (productVariantDetails) {
          productVariantDetails.forEach((productVariantDetail) => {
            const block_id = productVariantDetail.getAttribute('data-block-id');
            const metafieldDataElement = this.querySelector(`#product__metafield_for_variant-block--${block_id}`);
            if (!metafieldDataElement) {
              return;
            }
            try {
              let productVariantDetailsData = JSON.parse(metafieldDataElement?.textContent);
              productVariantDetail.innerHTML = productVariantDetailsData?.[variantId]?.replaceAll('\n', '<br />') || '';
            } catch (error) {
              console.error('Error parsing JSON', error);
            }
          });
        }
      }

      updateVariantDiscountBadge(price, compare_at_price) {
        const discountBadge = this.querySelector(".product-card__badge--discount");
        if (!discountBadge) return;


        if (compare_at_price > price) {
          discountBadge.classList.remove("hidden");
          const percentageElement = discountBadge.querySelector(".percentage");
          // calculate discount percentage
          const discountPercentage = Math.round(
            ((compare_at_price - price) / compare_at_price) * 100
          );
          // update discount percentage
          percentageElement.textContent = discountPercentage;
        } else {
          discountBadge.classList.add("hidden");
        }
      }

      setUnavailable() {
        if (this.productForm?.requiresVariantSelection && !this.productForm.hasSelectedVariant) {
          this.productForm.setSoftDisabledButtonState();
        } else {
          this.productForm?.toggleSubmitButton(
            true,
            window.variantStrings.unavailable
          );
        }

        const selectors = [
          "price",
          "Inventory",
          "Sku",
          "Quantity-Rules"
        ]
          .map(id => `#${id}-${this.dataset.section}`)
          .join(", ");
        document
          .querySelectorAll(selectors)
          .forEach(({ classList }) => classList.add("hidden"));
      }

      updateMedia(variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        if (
          this.querySelector(".main-product__media")?.hasAttribute(
            "data-desktop-grid"
          ) && window.innerWidth >= 750
        ) {
          this.scrollToImage(variantFeaturedMediaId);
        }

        const productMedia = this.querySelector(
          "swiper-product-gallery"
        );
        productMedia?.setActiveMedia(variantFeaturedMediaId);
      }

      scrollToImage(variantFeaturedMediaId) {
        this.querySelectorAll("[data-media-id]").forEach(img => {
          if (
            img.getAttribute("data-media-id") ==
            variantFeaturedMediaId
          ) {
            img.scrollIntoView({
              block: "end",
              behavior: "smooth"
            });
          }
        });
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity
            ? parseInt(this.quantityInput.dataset.cartQuantity)
            : 0,
          min: this.quantityInput.dataset.min
            ? parseInt(this.quantityInput.dataset.min)
            : 1,
          max: this.quantityInput.dataset.max
            ? parseInt(this.quantityInput.dataset.max)
            : null,
          step: this.quantityInput.step
            ? parseInt(this.quantityInput.step)
            : 1
        };

        let min = data.min;
        const max =
          data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min)
          min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute("max");
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId =
          this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector(
          ".quantity__rules-cart .loading__spinner"
        ).classList.remove("hidden");
        fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`
        )
          .then(response => response.text())
          .then(responseText => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch(e => console.error(e))
          .finally(() =>
            this.querySelector(
              ".quantity__rules-cart .loading__spinner"
            ).classList.add("hidden")
          );
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(
          `Quantity-Form-${sectionId}`
        );
        const selectors = [
          ".quantity__input",
          ".quantity__rules",
          ".quantity__label"
        ];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === ".quantity__input") {
            const attributes = [
              "data-cart-quantity",
              "data-min",
              "data-max",
              "step"
            ];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(
          `#ProductModal-${this.dataset.section}`
        );
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector("product-variant-selects");
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "related-products"
        );
        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`
        );
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "quick_order_list"
        );
        return document.querySelector(
          `quick-order-list[data-id^="${quickOrderListSectionId}"]`
        );
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

if (!customElements.get('product-form')) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.variantIdInput.disabled = false;
        this.form.addEventListener(
          "submit",
          this.onSubmitHandler.bind(this)
        );
        this.cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText =
          this.submitButton.querySelector("span");
        this.submitButtonSpinner =
          this.submitButton.querySelector(".loading__spinner");
        this.buttonsWrapper = this.querySelector(".product-form__buttons");
        this.buyNowButton = this.querySelector("#gokwik-buy-now");

        if (document.querySelector("cart-drawer"))
          this.setSubmitButtonsAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";
        this.buttonsWrapper?.addEventListener("click", this.onButtonsWrapperClick.bind(this));
        this.buyNowButton?.addEventListener("click", this.onBuyNowClick.bind(this), true);
        window.HallstattBuyNowGuard = window.HallstattBuyNowGuard || ((button) => {
          const productForm = button?.closest("product-form");
          if (productForm?.handleBuyNowButtonClick?.(button) === false) return false;
          if (typeof window.onBuyNowClick === "function") window.onBuyNowClick(button);
          return false;
        });
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.isSubmitting) return;

        if (this.requiresVariantSelection && this.hasMissingRequiredSize()) {
          if (this.variantIdInput) this.variantIdInput.value = "";
          this.showRequiredVariantMessage();
          return;
        }

        this.syncSelectedVariantToInput();

        if (this.requiresVariantSelection && !this.hasSelectedVariant) {
          this.showRequiredVariantMessage();
          return;
        }

        if (!this.validateQuantity()) return;
        if (!this.validateSellingPlan()) return;

        if (this.submitButton.getAttribute("aria-disabled") === "true") {
          this.showRequiredVariantMessage();
          return;
        }

        this.handleErrorMessage();
        this.error = false;
        this.isSubmitting = true;
        const hasRenderableCart =
          this.cart &&
          typeof this.cart.getSectionsToRender === "function" &&
          typeof this.cart.renderContents === "function";

        this.setSubmitButtonsAttribute("aria-disabled", "true");
        this.removeSubmitButtonsAttribute("data-missing-size-disabled");
        this.toggleSubmitButtonsClass("loading", true);
        this.submitButtonSpinner?.classList.remove(
          "hidden"
        );

        if (hasRenderableCart) {
          const cartDrawerItems = this.cart.querySelector(".cart-drawer-items");
          if (cartDrawerItems) {
            cartDrawerItems.classList.add("is-adding");
          }
        }

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);
        this.normalizeSellingPlanFormData(formData);
        if (hasRenderableCart) {
          formData.append(
            "sections",
            this.cart
              .getSectionsToRender()
              .map(section => section.section)
          );
          formData.append("sections_url", window.routes.cart_url);
          if (typeof this.cart.setActiveElement === "function") {
            this.cart.setActiveElement(document.activeElement);
          }
        }

        const addToCartRequest = this.buildAddToCartRequest(formData);
        if (addToCartRequest.contentType) {
          config.headers["Content-Type"] = addToCartRequest.contentType;
        }
        config.body = addToCartRequest.body;
        const addAbortController = new AbortController();
        const addAbortTimeout = window.setTimeout(() => {
          addAbortController.abort();
        }, 15000);
        config.signal = addAbortController.signal;

        fetch(`${routes.cart_add_url}`, config)
          .then(response => response.json())
          .then(async response => {
            if (response.status || response.errors) {
              const errorMessage = this.getCartErrorMessage(response);
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: addToCartRequest.productVariantId,
                errors: response.errors || response.description || errorMessage,
                message: response.message
              });
              this.handleErrorMessage(errorMessage);
              this.error = true;

              const soldOutMessage = this.submitButton.querySelector(
                ".sold-out-message"
              );
              if (!soldOutMessage) return;
              this.setSubmitButtonsAttribute("aria-disabled", "true");
              this.submitButtonText.classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              return;
            } else if (!hasRenderableCart) {
              window.location = window.routes.cart_url;
              return;
            }

            const cartState = await this.getCartState();
            const freshSections = await this.getFreshCartSections(response);
            const cartData = cartState || response;

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: addToCartRequest.productVariantId,
                cartData
              });
            this.error = false;
            this.cart.renderContents({
              ...response,
              sections: freshSections || response.sections,
              item_count: cartState?.item_count
            });
            if (cartState) {
              updateCartCounters(cartState);
              if (typeof updateFreeShipping === "function") updateFreeShipping(cartState);
            }
          })
          .catch(e => {
            console.error(e);
            this.error = true;
            const errorMessage =
              e?.name === "AbortError"
                ? "We couldn't add this item right now. Please try again."
                : window.cartStrings?.error || e?.message;
            this.handleErrorMessage(errorMessage);
          })
          .finally(() => {
            window.clearTimeout(addAbortTimeout);
            this.isSubmitting = false;
            this.toggleSubmitButtonsClass("loading", false);
            if (!this.error && this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.submitButton.hasAttribute("disabled") && !this.submitButton.hasAttribute("data-missing-size-disabled"))
              this.removeSubmitButtonsAttribute("aria-disabled");
            this.submitButtonSpinner?.classList.add(
              "hidden"
            );
            if (this.cart) {
              const cartDrawerItems = this.cart.querySelector(".cart-drawer-items");
              if (cartDrawerItems) cartDrawerItems.classList.remove("is-loading", "is-adding");
            }
          });
      }

      syncSelectedVariantToInput() {
        const variantSelects = this.closest("product-info")?.querySelector("product-variant-selects");
        variantSelects?.syncProductFormFromCurrentSelection?.();
      }

      validateQuantity() {
        const quantityInput = this.form.querySelector('[name="quantity"]');
        if (!quantityInput) return true;

        if (!quantityInput.value) {
          quantityInput.value = quantityInput.min || "1";
        }

        if (quantityInput.checkValidity()) return true;

        this.handleErrorMessage(quantityInput.validationMessage || window.cartStrings?.error);
        quantityInput.focus();
        return false;
      }

      validateSellingPlan() {
        const purchaseOptions = this.getPurchaseOptions();
        if (!purchaseOptions || typeof purchaseOptions.isOneTimePurchase !== "function") return true;
        if (purchaseOptions.isOneTimePurchase()) return true;

        if (this.getSelectedSellingPlanId()) return true;

        purchaseOptions.showError?.();
        this.handleErrorMessage(purchaseOptions.dataset.sellingPlanRequired || window.cartStrings?.error);
        return false;
      }

      normalizeSellingPlanFormData(formData) {
        formData.delete("selling_plan_group");
        formData.delete("selling_plan");

        const sellingPlanId = this.getSelectedSellingPlanId();
        if (sellingPlanId) {
          formData.append("selling_plan", sellingPlanId);
        }
      }

      buildAddToCartRequest(formData) {
        const giftBoxCheckbox = this.querySelector("[data-gift-box-checkbox][data-gift-box-variant-id]");
        const giftBoxVariantId = this.getPositiveInteger(giftBoxCheckbox?.dataset.giftBoxVariantId, 0);

        if (!giftBoxCheckbox?.checked || !giftBoxVariantId) {
          return {
            body: formData,
            productVariantId: formData.get("id")
          };
        }

        const mainCartItem = this.buildMainCartItem(formData);
        if (!mainCartItem) {
          return {
            body: formData,
            productVariantId: formData.get("id")
          };
        }

        const payload = {
          items: [
            mainCartItem,
            {
              id: giftBoxVariantId,
              quantity: this.getPositiveInteger(giftBoxCheckbox.dataset.giftBoxQuantity, 1)
            }
          ]
        };
        const sections = formData.get("sections");
        const sectionsUrl = formData.get("sections_url");

        if (sections) payload.sections = String(sections).split(",").filter(Boolean);
        if (sectionsUrl) payload.sections_url = sectionsUrl;

        return {
          body: JSON.stringify(payload),
          contentType: "application/json",
          productVariantId: formData.get("id")
        };
      }

      buildMainCartItem(formData) {
        const id = this.getPositiveInteger(formData.get("id"), 0);
        if (!id) return null;

        const item = {
          id,
          quantity: this.getPositiveInteger(formData.get("quantity"), 1)
        };
        const sellingPlan = formData.get("selling_plan");
        const properties = this.getLineItemProperties(formData);

        if (sellingPlan) item.selling_plan = sellingPlan;
        if (properties) item.properties = properties;

        return item;
      }

      getLineItemProperties(formData) {
        const properties = {};

        formData.forEach((value, key) => {
          const propertyMatch = key.match(/^properties\[(.+)\]$/);
          if (!propertyMatch || value === "") return;

          properties[propertyMatch[1]] = value;
        });

        return Object.keys(properties).length ? properties : null;
      }

      getPositiveInteger(value, fallback = 1) {
        const number = Number(value);
        return Number.isFinite(number) && number > 0 ? Math.floor(number) : fallback;
      }

      getPurchaseOptions() {
        return this.closest("product-info")?.querySelector("purchase-options");
      }

      getSelectedSellingPlanId() {
        const purchaseOptions = this.getPurchaseOptions();
        if (!purchaseOptions || purchaseOptions.isOneTimePurchase?.()) return "";

        const activeSellingPlan = purchaseOptions.querySelector(
          '[data-selling-plans-wrapper]:not(.is-hidden) .product__purchase-group-selects:not(.is-hidden) [name="selling_plan"]'
        );

        return activeSellingPlan?.value || "";
      }

      getCartErrorMessage(response) {
        const errorMessage = response.description || response.message || response.errors;
        if (Array.isArray(errorMessage)) return errorMessage.join(", ");
        if (errorMessage && typeof errorMessage === "object") {
          return Object.values(errorMessage).flat().join(", ");
        }
        return errorMessage;
      }

      async getCartState() {
        const cartUrl = window.routes?.cart_url || `${window.Shopify?.routes?.root || "/"}cart`;
        const cartAjaxUrl = cartUrl.endsWith(".js") ? cartUrl : `${cartUrl}.js`;

        return this.fetchJsonWithTimeout(cartAjaxUrl, {
          headers: {
            Accept: "application/json"
          }
        });
      }

      async getFreshCartSections(addResponse) {
        if (!this.cart || typeof this.cart.getSectionsToRender !== "function") {
          return addResponse?.sections || null;
        }

        const sections = this.cart.getSectionsToRender().map(section => section.section);
        if (!sections.length) return addResponse?.sections || null;

        const params = new URLSearchParams({
          sections: sections.join(",")
        });

        const sectionsMarkup = await this.fetchJsonWithTimeout(
          `${window.routes.cart_url}?${params.toString()}`,
          {
            headers: {
              Accept: "application/json",
              "X-Requested-With": "XMLHttpRequest"
            }
          }
        );
        return this.hasRenderableCartSections(sectionsMarkup)
          ? sectionsMarkup
          : addResponse?.sections || null;
      }

      hasRenderableCartSections(sectionsMarkup) {
        if (!sectionsMarkup || !this.cart || typeof this.cart.getSectionsToRender !== "function") return false;

        return this.cart.getSectionsToRender().every(section => {
          const html = sectionsMarkup[section.section];
          if (!html) return false;

          return Boolean(
            new DOMParser()
              .parseFromString(html, "text/html")
              .querySelector(section.selector)
          );
        });
      }

      async fetchJsonWithTimeout(url, options = {}, timeout = 6000) {
        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => {
          controller.abort();
        }, timeout);

        try {
          const response = await fetch(url, {
            ...options,
            signal: controller.signal
          });
          if (!response.ok) return null;
          return response.json();
        } catch (error) {
          if (error?.name !== "AbortError") console.error(error);
          return null;
        } finally {
          window.clearTimeout(timeoutId);
        }
      }

      onButtonsWrapperClick(event) {
        if (!this.requiresVariantSelection || !this.hasMissingRequiredSize()) return;

        const buttonRect = this.submitButton.getBoundingClientRect();
        const clickedSubmitArea =
          event.clientX >= buttonRect.left &&
          event.clientX <= buttonRect.right &&
          event.clientY >= buttonRect.top &&
          event.clientY <= buttonRect.bottom;

        if (!clickedSubmitArea) return;

        event.preventDefault();
        event.stopPropagation();
        if (this.variantIdInput) this.variantIdInput.value = "";
        this.showRequiredVariantMessage();
      }

      onBuyNowClick(evt) {
        if (!this.requiresVariantSelection || this.skipBuyNowVariantCheck) return;

        if (this.handleBuyNowButtonClick(evt.currentTarget) === false) {
          evt.preventDefault();
          evt.stopImmediatePropagation();
        }
      }

      handleBuyNowButtonClick() {
        if (!this.requiresVariantSelection || this.skipBuyNowVariantCheck) return true;

        if (this.hasMissingRequiredSize()) {
          if (this.variantIdInput) this.variantIdInput.value = "";
          this.showRequiredVariantMessage();
          return false;
        }

        if (this.hasSelectedVariant) return true;

        this.showRequiredVariantMessage();
        return false;
      }

      hasMissingRequiredSize() {
        const sizeOptionGroup = this.getSizeOptionGroup();
        return Boolean(sizeOptionGroup && !this.hasSelectedSize(sizeOptionGroup));
      }

      showRequiredVariantMessage() {
        const sizeOptionGroup = this.getSizeOptionGroup();
        if (sizeOptionGroup && !this.hasSelectedSize(sizeOptionGroup)) {
          this.showButtonError("Please select the size first.");
          return;
        }

        this.handleErrorMessage("Please select a variant.");
      }

      showButtonError(message) {
        const errorMessage = this.closest("product-info")?.querySelector("[data-product-form-button-error]");
        if (!errorMessage) {
          this.handleErrorMessage(message);
          return;
        }

        errorMessage.textContent = message;
        errorMessage.hidden = false;
      }

      clearButtonError() {
        const errorMessage = this.closest("product-info")?.querySelector("[data-product-form-button-error]");
        if (errorMessage) errorMessage.hidden = true;
      }

      getSizeOptionGroup() {
        const productInfo = this.closest("product-info");
        const variantSelects = productInfo?.querySelector(".product__variant-picker product-variant-selects");
        const optionGroups = Array.from(variantSelects?.querySelectorAll(".product-form__input") || []);
        return optionGroups.find(optionGroup => this.isSizeVariantOptionGroup(optionGroup));
      }

      hasSelectedSize(sizeOptionGroup) {
        const select = sizeOptionGroup.querySelector("select");
        if (select) return Boolean(select.value);

        return Boolean(sizeOptionGroup.querySelector('input:checked:not([data-size-chart-size-radio])'));
      }

      isSizeVariantOptionGroup(optionGroup) {
        if (optionGroup.dataset.isSizeOption === "true") return true;

        return this.normalizeVariantOptionName(this.getVariantOptionName(optionGroup)) === "size";
      }

      resetVariantSelection() {
        if (!this.requiresVariantSelection) return;

        if (this.variantIdInput) {
          this.variantIdInput.value = "";
        }

        const productInfo = this.closest("product-info");
        const variantPicker = productInfo?.querySelector(".product__variant-picker");
        this.clearVariantPickerSelection(variantPicker);

        if (productInfo?.dataset?.url && productInfo.dataset.updateUrl !== "false") {
          productInfo.updateURL(productInfo.dataset.url, null);
        }
      }

      clearVariantPickerSelection(variantPicker) {
        if (!variantPicker) return;

        variantPicker.querySelectorAll('input[type="radio"]').forEach(input => {
          input.checked = false;
          input.removeAttribute("checked");
        });

        variantPicker.querySelectorAll(".product-form__input").forEach(optionGroup => {
          const selectedValueLabel = optionGroup.querySelector("[data-selected-value]");
          if (selectedValueLabel && !selectedValueLabel.querySelector(".product-option__swatch")) {
            selectedValueLabel.textContent = `Select ${this.getVariantOptionName(optionGroup)}`;
          }
        });

        variantPicker.querySelectorAll("select").forEach(select => {
          const placeholderOption = Array.from(select.options).find(option => option.value === "");
          Array.from(select.options).forEach(option => option.removeAttribute("selected"));

          if (placeholderOption) {
            placeholderOption.setAttribute("selected", "selected");
            select.value = "";
          } else {
            select.selectedIndex = -1;
          }

          select.closest("dropdown-input")?.update();
        });
      }

      getVariantOptionName(optionGroup) {
        const legend = optionGroup.querySelector("legend");
        const legendText = legend?.childNodes?.[0]?.textContent?.replace(":", "").trim();
        if (legendText) return legendText;

        const labelText = optionGroup.querySelector("label.form__label")?.textContent?.replace(":", "").trim();
        return labelText || "variant";
      }

      normalizeVariantOptionName(optionName) {
        return (optionName || "").trim().toLowerCase();
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;
        if (!errorMessage) this.clearButtonError();

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message"
          );

        this.errorMessageWrapper.toggleAttribute(
          "hidden",
          !errorMessage
        );

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      get submitButtons() {
        const buttons = new Set();
        if (this.submitButton) buttons.add(this.submitButton);

        if (this.form?.id) {
          document.querySelectorAll('[type="submit"][name="add"][form]').forEach(button => {
            if (button.getAttribute("form") === this.form.id) buttons.add(button);
          });
        }

        return [...buttons];
      }

      setSubmitButtonsAttribute(attribute, value) {
        this.submitButtons.forEach(button => button.setAttribute(attribute, value));
      }

      removeSubmitButtonsAttribute(attribute) {
        this.submitButtons.forEach(button => button.removeAttribute(attribute));
      }

      toggleSubmitButtonsClass(className, force) {
        this.submitButtons.forEach(button => button.classList.toggle(className, force));
      }

      updateSubmitButtonText(text) {
        this.submitButtons.forEach(button => {
          const buttonText = button === this.submitButton
            ? this.submitButtonText
            : button.querySelector("span");

          if (buttonText) buttonText.textContent = text;
        });
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.setSubmitButtonsAttribute("disabled", "disabled");
          this.removeSubmitButtonsAttribute("data-missing-size-disabled");
          if (text) this.updateSubmitButtonText(text);
        } else {
          this.removeSubmitButtonsAttribute("disabled");
          this.removeSubmitButtonsAttribute("aria-disabled");
          this.removeSubmitButtonsAttribute("data-missing-size-disabled");
          this.updateSubmitButtonText(this.hasAttribute(
            "data-has-selling-plan"
          )
            ? window.variantStrings.addSubscriptionToCart
            : window.variantStrings.addToCart);
        }
      }

      setSoftDisabledButtonState(text = window.variantStrings.addToCart) {
        this.removeSubmitButtonsAttribute("disabled");
        this.setSubmitButtonsAttribute("aria-disabled", "true");
        this.setSubmitButtonsAttribute("data-missing-size-disabled", "true");
        this.updateSubmitButtonText(text);
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }

      get requiresVariantSelection() {
        return this.hasAttribute("data-requires-variant-selection");
      }

      get hasSelectedVariant() {
        return Boolean(this.variantIdInput?.value);
      }

    }
  );
}
